import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';

import UserContext from '../../../context/UserContext';
import Program from '../../../Model/Program';
import useSessionStore from '../../../hooks/useSessionStore';
import useComponentMounted from '../../../hooks/useComponentMounted';
import PrimaryContextsReadyContext from '../PrimaryContextsReadyContext';
import ExternalCoachContext from '../ExternalCoachContext/ExternalCoachContext';
import ManageProgramContext, { initialValues } from './ManageProgramContext';

const ManageProgramContextProvider = ({
  children,
}) => {
  const [coachProgramsCollection, setCoachProgramsCollection] = useState(initialValues.coachProgramsCollection);
  const [publicProgramsCollection, setPublicProgramsCollection] = useState(initialValues.publicProgramsCollection);
  const [coachPrograms, setCoachPrograms] = useState(initialValues.coachPrograms);
  const [showArchivedCoachPrograms, setShowArchivedCoachPrograms] = useState(initialValues.showArchivedCoachPrograms);
  const [showCoachProgramsOnly, setShowCoachProgramsOnly] = useState(initialValues.showCoachProgramsOnly);
  const [equipmentList, setEquipmentList] = useState(initialValues.equipmentList);
  const [isReady, setIsReady] = useState(initialValues.isReady);

  const { isCoach } = useSessionStore();
  const isComponentMountedRef = useComponentMounted();

  const { readyForDeferredContexts } = useContext(PrimaryContextsReadyContext);
  const { userId: coachId } = useContext(UserContext);
  const { coachDoc } = useContext(ExternalCoachContext);

  // Load the program collection related to the coach.
  useEffect(() => {
    const init = async () => {
      const programsCol = await Program.getProgramsByCoach(coachId);
      // For isPublicTracksCoach coaches, their programs are already included in
      // getProgramsByCoach and also appear in public programs. Exclude them from
      // the public query to avoid fetching duplicates from Firestore.
      const excludeCoachId = coachDoc?.isPublicTracksCoach ? coachId : null;
      const publicProgramsCol = await Program.getPublicPrograms(isCoach, excludeCoachId);
      if (isComponentMountedRef.current) {
        setCoachProgramsCollection(programsCol);
        setPublicProgramsCollection(publicProgramsCol);
        setIsReady(true);
      }
    };

    // Initialize the context only when the primary contexts are ready.
    if (coachId && readyForDeferredContexts) {
      init();
    }
  }, [
    isComponentMountedRef,
    coachId,
    coachDoc,
    readyForDeferredContexts,
    isCoach,
  ]);

  useEffect(() => {
    const disposer = autorun(() => {
      const coachProgramsDocs = coachProgramsCollection.hasDocs ? coachProgramsCollection.docs.slice() : [];
      const publicProgramsDocs = publicProgramsCollection.hasDocs ? publicProgramsCollection.docs.slice() : [];
      const uniqueEquipmentMap = new Map();
      if (coachProgramsCollection.hasDocs) {
        coachProgramsCollection.docs.forEach((program) => {
          if (program.equipment.length > 0) {
            program.equipment.forEach((equipment) => {
              if (!uniqueEquipmentMap.has(equipment)) {
                uniqueEquipmentMap.set(equipment, {
                  value: equipment,
                  label: equipment,
                });
              }
            });
          }
        });
      }
      const uniqueEquipmentList = Array.from(uniqueEquipmentMap.values());
      setEquipmentList(uniqueEquipmentList);

      const filteredPublicPrograms = publicProgramsDocs.slice();
      coachProgramsDocs.forEach((coachProgram) => {
        const { originalProgram } = coachProgram.data || {};
        if (originalProgram) {
          const originalProgramIndex = filteredPublicPrograms.findIndex(
            (publicProgram) => publicProgram.id === originalProgram,
          );
          if (originalProgramIndex >= 0) {
            filteredPublicPrograms.splice(originalProgramIndex, 1);
          }
        }
      });

      /*
       * When an internal user (AC/admin) enables "coach programs only", emulate
       * exactly what the coach sees: their own programs plus only the
       * coach-visible (isCoachOnly) public programs — the same set a coach
       * session would get via getPublicPrograms(isCoach=true). AC-only,
       * track-backing public programs are hidden. The excludeCoachId logic in
       * the fetch keeps this correct for isPublicTracksCoach coaches too.
       * Otherwise (default AC/admin view) show all public programs.
       */
      const visiblePublicPrograms = showCoachProgramsOnly
        ? filteredPublicPrograms.filter((program) => program.isCoachOnly)
        : filteredPublicPrograms;

      setCoachPrograms([...coachProgramsDocs, ...visiblePublicPrograms]);
    });
    return disposer;
  }, [
    coachProgramsCollection,
    isComponentMountedRef,
    publicProgramsCollection,
    showCoachProgramsOnly,
  ]);

  const refetchCoachPrograms = useCallback(async (fetchArchivedPrograms = false) => {
    setShowArchivedCoachPrograms(fetchArchivedPrograms);
    const programsCol = await Program.getProgramsByCoach(coachId, fetchArchivedPrograms);
    if (isComponentMountedRef.current) {
      setCoachProgramsCollection(programsCol);
    }
  }, [
    coachId,
    isComponentMountedRef,
  ]);

  const value = useMemo(() => ({
    coachProgramsCollection,
    isReady,
    coachPrograms,
    showArchivedCoachPrograms,
    refetchCoachPrograms,
    showCoachProgramsOnly,
    setShowCoachProgramsOnly,
    equipmentList,
  }), [
    coachProgramsCollection,
    isReady,
    coachPrograms,
    showArchivedCoachPrograms,
    refetchCoachPrograms,
    showCoachProgramsOnly,
    equipmentList,
  ]);

  return (
    <ManageProgramContext.Provider value={value}>
      {children}
    </ManageProgramContext.Provider>
  );
};

ManageProgramContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(ManageProgramContextProvider);
